/**
 * Is this event aimed at something the user types or picks into?
 *
 * THE one predicate for "this keypress belongs to a control, not the floor".
 * It used to exist twice - Player.ts checked INPUT|TEXTAREA, main.ts checked
 * INPUT|TEXTAREA|SELECT - and the two had already drifted. Ant Design widens
 * the problem: its Select, pickers and number inputs render as div composites
 * (often portalled to <body>), so a tag-name test alone lets WASD leak into
 * the walk controller while a student is choosing from a dropdown.
 *
 * Lives in core/, not ui/ or app/, because the walk controller in fab/ needs
 * it too and fab must stay importable by the headless check harnesses - this
 * file must never grow a React or DOM-construction import.
 *
 * The `.ant-*` class fallback is coupled to Ant Design v5's class prefix; if
 * antd is ever upgraded past v5, re-verify these names.
 */

const TYPING_TAGS = /^(INPUT|TEXTAREA|SELECT|OPTION)$/
const TYPING_ROLES = new Set([
  'textbox',
  'combobox',
  'searchbox',
  'spinbutton',
  'listbox',
  'menuitem',
])
const ANT_SURFACES =
  '.ant-select, .ant-picker, .ant-input-number, .ant-dropdown, .ant-modal, .ant-drawer, .ant-popover'

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  if ((target as HTMLElement).isContentEditable) return true
  if (TYPING_TAGS.test(target.tagName)) return true
  const role = target.getAttribute('role')
  if (role && TYPING_ROLES.has(role)) return true
  return target.closest(ANT_SURFACES) !== null
}
